import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Paper, Tooltip, Typography } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useNotify } from '@/stores/notificationStore';
import {
  BULK_WORK_ORDER_PDF_MAX,
  buildBulkWorkOrderPdf,
  downloadBlob,
  type BulkPdfTarget,
} from '@/lib/bulkWorkOrderPdf';

interface WorkOrderPdfSelectionBarProps {
  /** Selected work orders in list order. */
  selected: BulkPdfTarget[];
  /** Rows matching the current filters, across every page. */
  matchingTotal: number;
  /** True when every row on the current page is selected. */
  pageFullySelected: boolean;
  onSelectAllMatching: () => Promise<void>;
  onClear: () => void;
}

/** Local date as YYYY-MM-DD for the file name. */
function todayStamp(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Shown above the work order table while rows are selected (#4031): the
 * count, "select all N matching", and the button that downloads the selected
 * work orders as one PDF.
 */
export function WorkOrderPdfSelectionBar({
  selected,
  matchingTotal,
  pageFullySelected,
  onSelectAllMatching,
  onClear,
}: WorkOrderPdfSelectionBarProps) {
  const { t } = useTranslation('workorders');
  const notify = useNotify();
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [selectingAll, setSelectingAll] = useState(false);

  const count = selected.length;
  const tooMany = count > BULK_WORK_ORDER_PDF_MAX;
  const busy = progress !== null || selectingAll;
  const moreToSelect = pageFullySelected && matchingTotal > count;

  const handleDownload = async () => {
    setProgress({ done: 0, total: count });
    try {
      const { pdf, failed } = await buildBulkWorkOrderPdf(selected, (done, total) => {
        setProgress({ done, total });
      });
      if (!pdf) {
        notify.error(t('bulkPdf.failedAll'));
        return;
      }
      downloadBlob(pdf, `${t('bulkPdf.filename')}-${todayStamp()}.pdf`);
      if (failed.length > 0) {
        notify.warning(
          t('bulkPdf.partial', { count: failed.length }),
          failed.map((target) => target.label).join(', '),
        );
      } else {
        notify.success(t('bulkPdf.done', { count }));
      }
    } catch {
      notify.error(t('bulkPdf.failedAll'));
    } finally {
      setProgress(null);
    }
  };

  const handleSelectAll = async () => {
    setSelectingAll(true);
    try {
      await onSelectAllMatching();
    } catch {
      notify.error(t('bulkPdf.selectAllFailed'));
    } finally {
      setSelectingAll(false);
    }
  };

  const downloadButton = (
    <Button
      variant="contained"
      size="small"
      startIcon={progress ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
      disabled={busy || tooMany}
      onClick={() => void handleDownload()}
    >
      {progress
        ? t('bulkPdf.progress', { done: progress.done, total: progress.total })
        : t('bulkPdf.download')}
    </Button>
  );

  return (
    <Paper
      variant="outlined"
      role="region"
      aria-label={t('bulkPdf.regionLabel')}
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.5,
        px: 2,
        py: 1,
        mb: 2,
        bgcolor: 'action.selected',
      }}
    >
      <Typography variant="body2" fontWeight={600}>
        {t('bulkPdf.selectedCount', { count })}
      </Typography>

      {moreToSelect && (
        matchingTotal <= BULK_WORK_ORDER_PDF_MAX ? (
          <Button size="small" disabled={busy} onClick={() => void handleSelectAll()}>
            {t('bulkPdf.selectAllMatching', { count: matchingTotal })}
          </Button>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('bulkPdf.matchingOverLimit', { count: matchingTotal, max: BULK_WORK_ORDER_PDF_MAX })}
          </Typography>
        )
      )}

      <Box sx={{ flex: 1 }} />

      <Button size="small" disabled={busy} onClick={onClear}>
        {t('bulkPdf.clearSelection')}
      </Button>
      {tooMany ? (
        <Tooltip title={t('bulkPdf.tooMany', { max: BULK_WORK_ORDER_PDF_MAX })}>
          {/* A disabled button fires no events; the span carries the tooltip. */}
          <span>{downloadButton}</span>
        </Tooltip>
      ) : (
        downloadButton
      )}
    </Paper>
  );
}
